if (!customElements.get('comparison-table')) {

class ComparisonTable extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-trigger]');
    this.expandables = this.querySelectorAll('[data-expandable]');
    this.label = this.querySelector('[data-label]');
    this.icon = this.querySelector('[data-icon]');

    if (this.trigger) {
      this.trigger.addEventListener('click', () => this.toggle());
    }

    this.querySelectorAll('[data-swatch]').forEach((swatch) => {
      const handle = swatch.dataset.swatch;
      const src = swatch.dataset.image;
      if (!handle || !src) return;

      const activate = () => {
        this.setActiveSwatch(handle, swatch);
        this.setImage(handle, src);
      };
      const restore = () => {
        this.restoreActiveSwatch(handle);
        this.resetImage(handle);
      };

      swatch.addEventListener('mouseenter', activate);
      swatch.addEventListener('focus', activate);
      swatch.addEventListener('click', activate);
      swatch.addEventListener('mouseleave', restore);
      swatch.addEventListener('blur', restore);
    });

    this.scroll = this.querySelector('[data-scroll]');
    this.dots = this.querySelectorAll('[data-dot]');
    this.articles = this.scroll ? this.scroll.querySelectorAll('article') : [];

    if (this.scroll && this.dots.length && this.articles.length) {
      this.setupDots();
    }
  }

  setupDots() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const index = Array.from(this.articles).indexOf(entry.target);
            this.activateDot(index);
          }
        });
      },
      { root: this.scroll, threshold: [0.6] }
    );

    this.articles.forEach((article) => observer.observe(article));

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.scrollToCard(i));
    });

    this.activateDot(0);
  }

  scrollToCard(index) {
    const article = this.articles[index];
    if (!article || !this.scroll) return;
    const articleRect = article.getBoundingClientRect();
    const scrollRect = this.scroll.getBoundingClientRect();
    const targetLeft =
      this.scroll.scrollLeft +
      (articleRect.left - scrollRect.left) -
      (scrollRect.width - articleRect.width) / 2;
    this.scroll.scrollTo({ left: targetLeft, behavior: 'smooth' });
  }

  activateDot(index) {
    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  setActiveSwatch(handle, target) {
    this.querySelectorAll(`[data-swatch="${handle}"]`).forEach((swatch) => {
      if (swatch === target) {
        swatch.setAttribute('aria-current', 'true');
      } else {
        swatch.removeAttribute('aria-current');
      }
    });
  }

  restoreActiveSwatch(handle) {
    const swatches = this.querySelectorAll(`[data-swatch="${handle}"]`);
    swatches.forEach((swatch, i) => {
      if (i === 0) {
        swatch.setAttribute('aria-current', 'true');
      } else {
        swatch.removeAttribute('aria-current');
      }
    });
  }

  setImage(handle, src) {
    this.querySelectorAll(`[data-product-image="${handle}"]`).forEach((img) => {
      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.getAttribute('src') || '';
        img.dataset.originalSrcset = img.getAttribute('srcset') || '';
      }
      if (img.dataset.currentSrc === src) return;
      img.dataset.currentSrc = src;
      this.fadeSwap(img, src, '');
    });
  }

  resetImage(handle) {
    this.querySelectorAll(`[data-product-image="${handle}"]`).forEach((img) => {
      if (!img.dataset.originalSrc) return;
      const originalSrc = img.dataset.originalSrc;
      if (img.dataset.currentSrc === originalSrc) return;
      img.dataset.currentSrc = originalSrc;
      this.fadeSwap(img, originalSrc, img.dataset.originalSrcset || '');
    });
  }

  fadeSwap(img, src, srcset) {
    const preload = new Image();
    const apply = () => {
      if (img.dataset.currentSrc !== src) return;
      img.classList.add('opacity-0');
      setTimeout(() => {
        if (img.dataset.currentSrc !== src) return;
        if (srcset) {
          img.setAttribute('srcset', srcset);
        } else {
          img.removeAttribute('srcset');
        }
        img.setAttribute('src', src);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => img.classList.remove('opacity-0'));
        });
      }, 150);
    };
    preload.onload = apply;
    preload.onerror = apply;
    preload.src = src;
  }

  toggle() {
    const expanded = this.trigger.getAttribute('aria-expanded') === 'true';

    this.expandables.forEach((el) => {
      el.classList.toggle('hidden', expanded);
      if (el.classList.contains('flex-col')) {
        el.classList.toggle('flex', !expanded);
      }
    });

    this.trigger.setAttribute('aria-expanded', String(!expanded));

    if (this.label) {
      this.label.textContent = expanded ? 'Expand to view all details' : 'Collapse details';
    }

    if (this.icon) {
      this.icon.style.transform = expanded ? '' : 'rotate(180deg)';
    }
  }
}

customElements.define('comparison-table', ComparisonTable);

}
